import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { activeRackTypesList } from "@/data/rackTypes";

import selectiveImg from "@/assets/products/selective.png";
import cantileverImg from "@/assets/products/cantilever.png";
import vnaImg from "@/assets/products/vna.png";
import palletFlowImg from "@/assets/products/pallet-flow.png";
import asrsImg from "@/assets/products/asrs.png";
import doubleDeepImg from "@/assets/products/double-deep.png";
import driveInImg from "@/assets/products/drive-in.png";
import stackRacksImg from "@/assets/products/stack-racks.png";
import mobileImg from "@/assets/products/mobile.png";
import pushBackImg from "@/assets/products/push-back.png";
import shuttleImg from "@/assets/products/shuttle.png";
import postPalletsImg from "@/assets/products/post-pallets.png";
import cladRackImg from "@/assets/products/clad-rack.png";

const productImages: Record<string, string> = {
  selective: selectiveImg,
  cantilever: cantileverImg,
  vna: vnaImg,
  "pallet-flow": palletFlowImg,
  asrs: asrsImg,
  "double-deep": doubleDeepImg,
  "drive-in": driveInImg,
  "drive-through": driveInImg,
  "stack-racks": stackRacksImg,
  mobile: mobileImg,
  "push-back": pushBackImg,
  shuttle: shuttleImg,
  "post-pallets": postPalletsImg,
  "clad-rack": cladRackImg,
};
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import AutoScroll from "embla-carousel-auto-scroll";
import { useRef } from "react";

export function RackTypes() {
  const autoScrollPlugin = useRef(
    AutoScroll({ speed: 0.8, stopOnInteraction: false, stopOnMouseEnter: true, startDelay: 200 })
  );

  return (
    <section className="py-16 md:py-24 bg-primary text-primary-foreground">
      <div className="container">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent/20 text-accent text-sm font-semibold mb-4">
            Product Catalog
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Pallet Racking Systems for Every Need
          </h2>
          <p className="text-lg text-primary-foreground/70 max-w-2xl mx-auto">
            From standard selective to specialized high-density solutions—we source and supply it all.
          </p>
        </div>

      </div>

      {/* Carousel - edge to edge */}
      <div className="w-full overflow-hidden">
        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          plugins={[autoScrollPlugin.current]}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {activeRackTypesList.map((rack, index) => (
              <CarouselItem
                key={rack.id}
                className="pl-4 basis-[80%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4 2xl:basis-1/5"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
                  className="h-full"
                >
                  <Link
                    to={`/products/${rack.slug}`}
                    className="group block h-full p-5 rounded-2xl bg-primary-foreground/5 border border-primary-foreground/10 hover:bg-primary-foreground/10 hover:border-accent/50 transition-all duration-300"
                  >
                    <div className="aspect-[4/3] rounded-xl bg-primary-foreground/10 mb-4 flex items-center justify-center overflow-hidden">
                      {productImages[rack.slug] ? (
                        <img
                          src={productImages[rack.slug]}
                          alt={`${rack.name} system`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          width={800}
                          height={600}
                        />
                      ) : (
                        <div className="text-3xl font-black text-primary-foreground/20 group-hover:text-accent/30 transition-colors text-center px-2">
                          {rack.name.split(" ")[0]}
                        </div>
                      )}
                    </div>

                    <h3 className="text-lg font-bold text-primary-foreground mb-1.5 group-hover:text-accent transition-colors line-clamp-1">
                      {rack.name}
                    </h3>

                    <p className="text-primary-foreground/60 text-sm mb-3 line-clamp-2">
                      {rack.tagline}
                    </p>

                    {/* Ratings */}
                    <div className="flex items-center gap-4 mb-3 text-xs text-primary-foreground/50">
                      <span>Density: <strong className="text-primary-foreground">{rack.densityRating}/5</strong></span>
                      <span>Access: <strong className="text-primary-foreground">{rack.accessibilityRating}/5</strong></span>
                    </div>

                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent group-hover:gap-2.5 transition-all">
                      Learn More
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </motion.div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      <div className="container">

        {/* CTA */}
        <div className="text-center mt-12">
          <Button variant="accent" size="lg" asChild>
            <Link to="/products">
              View All Products
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
